import { EditarRequisitoModal } from '@/components/sysadmin/EditarRequisitoModal';
import { Badge, Card, Input, LoadingContainer, Select } from '@/components/ui';
import { useNtcsDisponibles, useRequisitosDisponibles } from '@/hooks/useSysadmin';
import type { RequisitoSysadmin } from '@/services/sysadmin.service';
import { Pencil, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

// ========================================
// Componente: Tabla de requisitos globales
// ========================================

interface RequisitosGlobalTableProps {
  requisitos: RequisitoSysadmin[];
  onEditar: (requisito: RequisitoSysadmin) => void;
}

function RequisitosGlobalTable({ requisitos, onEditar }: RequisitosGlobalTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm table-fixed" style={{ minWidth: '800px' }}>
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[10%]">NTC</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[8%]">Capitulo</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[8%]">Numeral</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[38%]">Descripcion</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-[28%]">Evidencia Sugerida</th>
            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-[8%]">Accion</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {requisitos.map((req) => (
            <tr key={req.requisitoId} className="hover:bg-gray-50">
              <td className="px-3 py-2 overflow-hidden">
                <span className="text-xs font-medium text-gray-700 wrap-break-word">{req.ntcNombre}</span>
              </td>
              <td className="px-3 py-2 overflow-hidden">
                <span className="text-gray-900 font-mono text-xs wrap-break-word break-all">{req.capitulo}</span>
              </td>
              <td className="px-3 py-2 overflow-hidden">
                <span className="text-gray-900 font-mono text-xs wrap-break-word break-all">{req.numeral}</span>
              </td>
              <td className="px-3 py-2 overflow-hidden">
                <p className="text-gray-700 wrap-break-word whitespace-pre-line">{req.descripcion}</p>
              </td>
              <td className="px-3 py-2 overflow-hidden">
                {req.posibleEvidencia ? (
                  <p className="text-gray-500 text-xs wrap-break-word whitespace-normal">{req.posibleEvidencia}</p>
                ) : (
                  <span className="text-gray-300 text-xs">--</span>
                )}
              </td>
              <td className="px-3 py-2 text-center">
                <button
                  type="button"
                  onClick={() => onEditar(req)}
                  className="inline-flex items-center justify-center p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                  title="Editar requisito"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ========================================
// Pagina principal: Editar Requisitos
// ========================================

export function EditarRequisitosPage() {
  const { ntcs } = useNtcsDisponibles();
  const [selectedNtcId, setSelectedNtcId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [editingRequisito, setEditingRequisito] = useState<RequisitoSysadmin | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const { requisitos, isLoading, error, actualizarRequisito } = useRequisitosDisponibles(selectedNtcId);

  const filteredRequisitos = useMemo(() => {
    if (!search) return requisitos;
    const q = search.toLowerCase();
    return requisitos.filter(
      (r) =>
        r.descripcion.toLowerCase().includes(q) ||
        r.numeral.toLowerCase().includes(q) ||
        r.capitulo.toLowerCase().includes(q) ||
        r.ntcNombre.toLowerCase().includes(q) ||
        (r.posibleEvidencia && r.posibleEvidencia.toLowerCase().includes(q))
    );
  }, [requisitos, search]);

  const stats = useMemo(() => {
    const total = requisitos.length;
    const conEvidencia = requisitos.filter((r) => r.posibleEvidencia).length;
    const sinEvidencia = total - conEvidencia;
    return { total, conEvidencia, sinEvidencia };
  }, [requisitos]);

  const handleEditar = (requisito: RequisitoSysadmin) => {
    setEditingRequisito(requisito);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setEditingRequisito(null);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingRequisito(null);
  };

  if (isLoading) return <LoadingContainer text="Cargando requisitos..." />;
  if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

  return (
    <div className="space-y-4">
      {/* Encabezado */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Editar Requisitos</h2>
        <p className="text-sm text-gray-500">
          Modifica los datos de los requisitos del sistema. Los cambios se aplican globalmente a todas las empresas.
        </p>
      </div>

      {/* Estadisticas */}
      <div className="flex gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Badge variant="blue">{stats.total}</Badge>
          <span className="text-gray-600">Total</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="green">{stats.conEvidencia}</Badge>
          <span className="text-gray-600">Con evidencia</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="gray">{stats.sinEvidencia}</Badge>
          <span className="text-gray-600">Sin evidencia</span>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <div className="relative flex-1 w-full sm:max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por descripcion, numeral, capitulo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          classNameDiv="w-fit"
          value={selectedNtcId?.toString() || ''}
          onChange={(e) => {
            setSelectedNtcId(e.target.value ? parseInt(e.target.value) : undefined);
          }}
          options={[{ value: '', label: 'Todas las NTCs' }, ...ntcs.map((n) => ({ value: n.ntcId.toString(), label: n.nombre }))]}
        />
      </div>

      {/* Tabla */}
      <Card>
        {filteredRequisitos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No se encontraron requisitos con los filtros actuales</p>
          </div>
        ) : (
          <RequisitosGlobalTable requisitos={filteredRequisitos} onEditar={handleEditar} />
        )}
      </Card>

      <p className="text-xs text-gray-400 text-right">{filteredRequisitos.length} requisitos mostrados</p>

      {/* Modal de edicion */}
      <EditarRequisitoModal
        isOpen={showEditModal}
        onClose={handleCloseEdit}
        onSuccess={handleEditSuccess}
        requisito={editingRequisito}
        onSave={actualizarRequisito}
      />
    </div>
  );
}
